import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { List, Loader2, Map as MapIcon, MapPin } from 'lucide-react'

import { MapView } from '../components/MapView'
import { LegalFooter } from '../components/layout/LegalFooter'
import { formatApiError, useAuthStatus, useLocations } from '../shared/api/client'
import type { LocationListItem } from '../shared/api/types'
import {
  formatAverage,
  formatRegion,
  formatVerificationLevel,
} from '../shared/lib/location-formatters'
import { trackEvent } from '../shared/lib/analytics'
import { buildWebSiteStructuredData, Seo } from '../shared/lib/seo'
import { buildSearchParams } from '../shared/lib/search-params'

function ActiveFilters({
  searchParams,
  setSearchParams,
}: {
  searchParams: URLSearchParams
  setSearchParams: ReturnType<typeof useSearchParams>[1]
}) {
  const activeEntries = [
    searchParams.get('q')
      ? { key: 'q', label: `Iskanje: ${searchParams.get('q')}` }
      : null,
    searchParams.get('region')
      ? { key: 'region', label: `Regija: ${searchParams.get('region')}` }
      : null,
    searchParams.get('warning')
      ? { key: 'warning', label: `Opozorilo: ${searchParams.get('warning')}` }
      : null,
    searchParams.get('sort') && searchParams.get('sort') !== 'recent'
      ? { key: 'sort', label: `Sort: ${searchParams.get('sort')}` }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string }>

  if (!activeEntries.length) {
    return (
      <p className="text-sm text-slate-500">
        Filtri usklajujejo seznam in markerje v istem pogledu brez menjave strani.
      </p>
    )
  }

  return (
    <div className="flex flex-wrap gap-2">
      {activeEntries.map((entry) => (
        <button
          key={entry.key}
          type="button"
          onClick={() =>
            setSearchParams(buildSearchParams(searchParams, { [entry.key]: '' }))
          }
          className="group rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-800 ring-1 ring-emerald-200 transition hover:bg-rose-50 hover:text-rose-700 hover:ring-rose-200"
        >
          {entry.label}
          <span className="ml-1 text-emerald-600 transition group-hover:text-rose-600">×</span>
        </button>
      ))}
      <button
        type="button"
        onClick={() => setSearchParams(new URLSearchParams())}
        className="rounded-full px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-50"
      >
        Počisti vse
      </button>
    </div>
  )
}

function LocationCard({ location }: { location: LocationListItem }) {
  return (
    <Link
      to={`/locations/${location.slug}`}
      className="group block rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-lg"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900 transition group-hover:text-emerald-700">
            {location.title}
          </h3>
          <p className="mt-1 text-sm text-slate-500">{formatRegion(location.region_code)}</p>
        </div>
        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
          {formatAverage(location.review_summary?.recommendation_average)}
        </span>
      </div>

      <p className="mt-3 text-sm leading-6 text-slate-600">
        {location.short_description ||
          'Opis še ni dodan. Lokacija je pripravljena za podrobnejši pregled.'}
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {(location.warning_terms ?? []).slice(0, 3).map((warning) => (
          <span
            key={warning.id}
            className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-900"
          >
            {warning.label}
          </span>
        ))}
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
          {formatVerificationLevel(location.verification_level)}
        </span>
      </div>
    </Link>
  )
}

export function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const authStatus = useAuthStatus()
  const locationsQuery = useLocations(searchParams)
  const locations = locationsQuery.data?.pages.flatMap((page) => page.results) ?? []
  const [mobileView, setMobileView] = useState<'list' | 'map'>('map')
  const fetchNextPage = locationsQuery.fetchNextPage
  const hasNextPage = locationsQuery.hasNextPage
  const isFetchingNextPage = locationsQuery.isFetchingNextPage
  const activeBbox = searchParams.get('bbox') ?? ''
  const sortValue = searchParams.get('sort') ?? 'recent'
  const filteredMarkersCount = locations.filter(
    (location) => location.latitude !== null && location.longitude !== null,
  ).length

  useEffect(() => {
    if (!activeBbox || !hasNextPage || isFetchingNextPage) {
      return
    }

    fetchNextPage()
  }, [activeBbox, fetchNextPage, hasNextPage, isFetchingNextPage])

  useEffect(() => {
    trackEvent('page.view', {
      page: '/',
      query: searchParams.get('q') ?? '',
      region: searchParams.get('region') ?? '',
      warning: searchParams.get('warning') ?? '',
      sort: sortValue,
    })
  }, [searchParams, sortValue])

  return (
    <main className="relative flex min-h-0 flex-1 overflow-hidden md:min-h-0" style={{ minHeight: '100dvh' }}>
      <Seo
        title="Zemljevid piknik lokacij"
        description="Odkrij preverjene piknik lokacije, filtriraj opozorila in razišči skupnostna priporočila po Sloveniji."
        path={searchParams.toString() ? `/?${searchParams.toString()}` : '/'}
        structuredData={buildWebSiteStructuredData()}
      />
      <section
        className={`absolute inset-x-0 bottom-0 top-[60px] z-[400] flex h-[calc(100dvh-60px)] w-full flex-col rounded-t-[28px] bg-slate-50 shadow-[0_-10px_40px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out md:relative md:top-0 md:h-full md:w-[420px] md:shrink-0 md:translate-y-0 md:rounded-none md:border-r md:border-slate-200 md:shadow-none lg:w-[480px] ${
          mobileView === 'list' ? 'translate-y-0' : 'translate-y-[calc(100%-6.5rem)]'
        }`}
      >
        <div className="border-b border-slate-200 bg-slate-50/95 px-4 pb-4 pt-5 backdrop-blur md:px-5 md:py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                Discovery Feed
              </p>
              <h1 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 md:text-2xl">
                Odkrij lokacije brez menjave pogleda.
              </h1>
            </div>
            <div className="rounded-[20px] bg-white px-3 py-2.5 text-right shadow-sm ring-1 ring-slate-200 md:rounded-[24px] md:px-4 md:py-3">
              <p className="text-2xl font-semibold text-slate-900">
                {locationsQuery.data?.pages[0]?.count ?? locations.length}
              </p>
              <p className="text-xs text-slate-500">zadetkov</p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <select
              aria-label="Sortiranje"
              value={sortValue}
              onChange={(event) =>
                setSearchParams(buildSearchParams(searchParams, { sort: event.target.value }))
              }
              className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10"
            >
              <option value="recent">Najnovejše</option>
              <option value="oldest">Najstarejše</option>
              <option value="title">Naslov A-Ž</option>
              <option value="trust">Zaupanje</option>
            </select>

            {locationsQuery.isFetching && activeBbox ? (
              <span className="text-sm text-slate-500">
                Nalagam vse markerje za trenutni pogled…
              </span>
            ) : locationsQuery.isFetching ? (
              <span className="text-sm text-slate-500">Osvežujem rezultate…</span>
            ) : (
              <span className="text-sm text-slate-500">
                Zemljevid in kartice uporabljajo iste filtre.
              </span>
            )}
          </div>

          <div className="mt-4">
            <ActiveFilters searchParams={searchParams} setSearchParams={setSearchParams} />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 pb-[calc(env(safe-area-inset-bottom)+7.5rem)] md:px-5 md:pb-4">
          {locationsQuery.isLoading && !locations.length ? (
            <div className="space-y-3">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="animate-pulse rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-2">
                      <div className="h-4 w-36 rounded-full bg-slate-200" />
                      <div className="h-3 w-24 rounded-full bg-slate-100" />
                    </div>
                    <div className="h-7 w-16 rounded-full bg-slate-100" />
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="h-3 w-full rounded-full bg-slate-100" />
                    <div className="h-3 w-4/5 rounded-full bg-slate-100" />
                  </div>
                  <div className="mt-4 flex gap-2">
                    <div className="h-6 w-20 rounded-full bg-amber-100" />
                    <div className="h-6 w-24 rounded-full bg-slate-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {locationsQuery.isError ? (
            <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
              {formatApiError(locationsQuery.error)}
            </div>
          ) : null}

          {!locationsQuery.isLoading && !locationsQuery.isError && !locations.length ? (
            <div className="rounded-[28px] border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
              <MapPin className="mx-auto text-slate-300" size={32} />
              <h2 className="mt-4 text-lg font-semibold text-slate-900">
                Na tem območju ni lokacij.
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Poskusi drugo regijo ali odstrani aktivna opozorila iz filtra.
              </p>
            </div>
          ) : null}

          <div className="space-y-3">
            {locations.map((location) => (
              <LocationCard key={location.id} location={location} />
            ))}
          </div>

          {!activeBbox && locationsQuery.hasNextPage ? (
            <div className="mb-8 mt-6 px-2 md:mb-4">
              <button
                type="button"
                onClick={() => locationsQuery.fetchNextPage()}
                disabled={locationsQuery.isFetchingNextPage}
                className="w-full rounded-2xl border-2 border-emerald-100 bg-emerald-50 px-4 py-4 text-base font-bold text-emerald-800 transition hover:border-emerald-200 hover:bg-emerald-100 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {locationsQuery.isFetchingNextPage ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={18} className="animate-spin" />
                    <span>Nalagam...</span>
                  </span>
                ) : (
                  'Prikaži več lokacij'
                )}
              </button>
            </div>
          ) : null}

          <div className="mt-6 md:hidden">
            <LegalFooter compact />
          </div>
        </div>
      </section>

      <section className="absolute inset-0 z-0 h-full w-full bg-slate-200 md:relative md:inset-auto md:flex-1 md:z-auto">
        <MapView locations={locations} syncBounds />

        <div className="pointer-events-none absolute left-4 top-4 z-[400] hidden flex-wrap gap-3 md:flex">
          <div className="pointer-events-auto rounded-[24px] bg-white/95 px-4 py-3 shadow-xl ring-1 ring-slate-200 backdrop-blur">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
              Markerji
            </p>
            <p className="mt-1 text-xl font-semibold text-slate-900">{filteredMarkersCount}</p>
          </div>
          <div className="pointer-events-auto rounded-[24px] bg-white/95 px-4 py-3 shadow-xl ring-1 ring-slate-200 backdrop-blur">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
              Status
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {authStatus.data?.authenticated ? 'Prijavljen uporabnik' : 'Gostujoči obiskovalec'}
            </p>
          </div>
        </div>
      </section>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[500] md:hidden">
        <div className="flex justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+5.5rem)] pt-12">
          <button
            type="button"
            onClick={() => setMobileView(mobileView === 'map' ? 'list' : 'map')}
            className="pointer-events-auto flex min-h-[52px] items-center gap-2 rounded-full bg-slate-900 px-6 py-3.5 text-sm font-bold text-white shadow-[0_20px_50px_rgba(15,23,42,0.28)] transition active:scale-95"
          >
            {mobileView === 'map' ? (
              <>
                <List size={18} />
                <span>Pokaži seznam</span>
              </>
            ) : (
              <>
                <MapIcon size={18} />
                <span>Zemljevid</span>
              </>
            )}
          </button>
        </div>
      </div>
    </main>
  )
}
